"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  api,
  loadTokens,
  type PrivacyPrefs,
  type ProfileVisibility,
  type SharePrefs,
} from "@/lib/api";
import { LocalePicker } from "@/components/LocalePicker";
import { Skeleton } from "@/components/Skeleton";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { formatMoney } from "@/lib/format";

const MAX_DISPLAY_NAME = 60;
const MAX_AVATAR_URL = 500;
const MAX_BIO = 280;

/**
 * EPIC-021 STORY-375 — profile editor.
 *
 * One PATCH per save (atomic). Optimistic local state with a small
 * "Saved" pulse + rollback on server error so the user sees something
 * even if their network is bad.
 *
 * Avatar today is paste-a-URL only. Cloudflare R2 / DOCR-backed
 * uploads ship at Gate B per STORY-375 spec — closed beta scope is
 * the data shape + the editing surface.
 */
export function ProfileSettings() {
  const { t } = useTranslation();
  const [authChecked, setAuthChecked] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [bio, setBio] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!loadTokens()) return;
    let cancelled = false;
    void api
      .me()
      .then((profile) => {
        if (cancelled) return;
        setUsername(profile.username);
        setDisplayName(profile.display_name ?? "");
        setAvatarUrl(profile.avatar_url ?? "");
        setBio(profile.bio ?? "");
        setAuthChecked(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : t("profileSettings.loadFailed"),
        );
      });
    return () => {
      cancelled = true;
    };
    // `t` is stable per react-i18next contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Empty strings → null so the column actually clears, not a
      // zero-length string getting persisted.
      const updated = await api.updateProfile({
        displayName: displayName.trim() === "" ? null : displayName.trim(),
        avatarUrl: avatarUrl.trim() === "" ? null : avatarUrl.trim(),
        bio: bio.trim() === "" ? null : bio.trim(),
      });
      setDisplayName(updated.displayName ?? "");
      setAvatarUrl(updated.avatarUrl ?? "");
      setBio(updated.bio ?? "");
      setSavedAt(Date.now());
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("profileSettings.saveFailed");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!authChecked) {
    return (
      <div
        role="status"
        aria-label={t("profileSettings.loadingAria")}
        className="space-y-3"
      >
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-9 w-full rounded-md" />
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-9 w-full rounded-md" />
        <Skeleton className="h-9 w-32 rounded-md" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Field
        label={t("profileSettings.username")}
        helpText={t("profileSettings.usernameHelp")}
      >
        <input
          type="text"
          value={username ?? ""}
          disabled
          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text3)]"
        />
      </Field>

      <Field
        label={t("profileSettings.displayName")}
        helpText={t("profileSettings.displayNameHelpTemplate", {
          count: displayName.length,
          max: MAX_DISPLAY_NAME,
        })}
      >
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value.slice(0, MAX_DISPLAY_NAME))}
          disabled={submitting}
          placeholder={username ?? ""}
          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
        />
      </Field>

      <Field
        label={t("profileSettings.avatarUrl")}
        helpText={t("profileSettings.avatarUrlHelp")}
      >
        <div className="flex items-center gap-3">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={t("profileSettings.avatarPreviewAlt")}
              width={48}
              height={48}
              className="h-12 w-12 rounded-full border border-[var(--color-border)] object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full text-[20px] font-bold"
              style={{
                background:
                  "color-mix(in srgb, var(--color-brand) 14%, transparent)",
                color: "var(--color-brand)",
              }}
              aria-hidden="true"
            >
              {username?.charAt(0).toUpperCase() ?? "?"}
            </div>
          )}
          <input
            type="url"
            value={avatarUrl}
            onChange={(e) => setAvatarUrl(e.target.value.slice(0, MAX_AVATAR_URL))}
            disabled={submitting}
            placeholder={t("profileSettings.avatarPlaceholder")}
            className="flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
          />
        </div>
      </Field>

      <Field
        label={t("profileSettings.bio")}
        helpText={t("profileSettings.bioHelpTemplate", {
          count: bio.length,
          max: MAX_BIO,
        })}
      >
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value.slice(0, MAX_BIO))}
          disabled={submitting}
          rows={3}
          placeholder={t("profileSettings.bioPlaceholder")}
          className="w-full resize-none rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
        />
      </Field>

      {error && (
        <p
          role="alert"
          className="text-[12px]"
          style={{ color: "var(--color-no)" }}
        >
          {error}
        </p>
      )}

      {savedAt && !error && (
        <p
          className="text-[11px]"
          style={{ color: "var(--color-yes)" }}
          aria-live="polite"
          key={savedAt}
        >
          {t("profileSettings.saved")}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md px-4 py-2 text-[12px] font-bold text-white transition-colors disabled:opacity-50"
          style={{ background: "var(--color-brand)" }}
        >
          {submitting ? t("profileSettings.saving") : t("profileSettings.save")}
        </button>
      </div>

      {/* EPIC-028 STORY-1604 — locale picker */}
      <hr className="my-2 border-[var(--color-border)]" />
      <LocalePicker variant="full" />

      {/* EPIC-006 STORY-611 — win-share preferences */}
      <hr className="my-2 border-[var(--color-border)]" />
      <SharePrefsSection />

      {/* EPIC-021 STORY-378 — privacy preferences */}
      <hr className="my-2 border-[var(--color-border)]" />
      <PrivacyPrefsSection />

      {/* EPIC-022 STORY-628 — creator subscription price */}
      <hr className="my-2 border-[var(--color-border)]" />
      <SubscriptionPriceSection />
    </form>
  );
}

function SubscriptionPriceSection() {
  const { t } = useTranslation();
  const displayCurrency = useDisplayCurrency();
  const [priceCents, setPriceCents] = useState<number | null>(null);
  const [draft, setDraft] = useState<string>("");
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .me()
      .then((u) =>
        api.getPublicProfile(u.username).then((res) => {
          if (cancelled) return;
          if ("accessGated" in res) return;
          const cents = res.user.subscriptionPriceCents;
          setPriceCents(cents === null ? null : Number(cents));
          setDraft(
            cents === null ? "" : String(Math.round(Number(cents) / 100)),
          );
          setLoaded(true);
        }),
      )
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSave(disable: boolean) {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      let nextCents: number | null = null;
      if (!disable) {
        const reais = parseInt(draft, 10);
        if (!Number.isFinite(reais) || reais < 1 || reais > 10000) {
          throw new Error(t("subscriptionPrice.errPriceRange"));
        }
        nextCents = reais * 100;
      }
      const res = await api.setMySubscriptionPrice(nextCents);
      const newCents = res.priceCents === null ? null : Number(res.priceCents);
      setPriceCents(newCents);
      setDraft(newCents === null ? "" : String(Math.round(newCents / 100)));
      setSavedAt(Date.now());
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("subscriptionPrice.saveFailed"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!loaded) {
    return (
      <p className="text-[11px] text-[var(--color-text3)]">
        {t("subscriptionPrice.loading")}
      </p>
    );
  }

  return (
    <div>
      <h3 className="mb-1 text-[12px] font-bold text-[var(--color-text)]">
        {t("subscriptionPrice.title")}
      </h3>
      <p className="mb-2 text-[11px] text-[var(--color-text3)]">
        {t("subscriptionPrice.body")}
      </p>

      {error && (
        <p
          role="alert"
          className="mb-2 text-[11px]"
          style={{ color: "var(--color-no)" }}
        >
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
            {t("subscriptionPrice.priceLabel")}
          </label>
          <input
            type="number"
            min={1}
            max={10000}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={submitting}
            placeholder="0"
            className="w-32 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 font-mono text-[13px] tabular-nums outline-none focus:border-[var(--color-brand)]"
          />
        </div>
        <button
          type="button"
          onClick={() => handleSave(false)}
          disabled={submitting || draft.trim() === ""}
          className="rounded-md px-3 py-2 text-[11px] font-bold text-white transition-colors disabled:opacity-50"
          style={{ background: "var(--color-brand)" }}
        >
          {submitting
            ? t("subscriptionPrice.saving")
            : priceCents === null
              ? t("subscriptionPrice.enable")
              : t("subscriptionPrice.update")}
        </button>
        {priceCents !== null && (
          <button
            type="button"
            onClick={() => handleSave(true)}
            disabled={submitting}
            className="rounded-md border border-[var(--color-border)] px-3 py-2 text-[11px] font-bold text-[var(--color-text2)]"
          >
            {t("subscriptionPrice.disable")}
          </button>
        )}
      </div>

      {priceCents !== null && (
        <p className="mt-2 text-[11px] text-[var(--color-text3)]">
          {t("subscriptionPrice.activeTemplate", {
            amount: formatMoney(priceCents, displayCurrency),
          })}
        </p>
      )}
      {savedAt && (
        <p
          className="mt-1 text-[11px]"
          style={{ color: "var(--color-yes)" }}
          aria-live="polite"
          key={savedAt}
        >
          {t("subscriptionPrice.saved")}
        </p>
      )}
    </div>
  );
}


function PrivacyPrefsSection() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState<PrivacyPrefs | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .getPrivacyPrefs()
      .then((p) => {
        if (!cancelled) setPrefs(p);
      })
      .catch(() => {
        // Silent — section stays in loading state.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function applyPatch(patch: Partial<PrivacyPrefs>, key: string) {
    if (!prefs || pendingKey) return;
    const optimistic: PrivacyPrefs = { ...prefs, ...patch };
    setPrefs(optimistic);
    setPendingKey(key);
    setError(null);
    try {
      const updated = await api.updatePrivacyPrefs(patch);
      setPrefs(updated);
    } catch (err) {
      setPrefs(prefs);
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("privacyPrefs.saveFailed"),
      );
    } finally {
      setPendingKey(null);
    }
  }

  if (!prefs) {
    return (
      <p className="text-[11px] text-[var(--color-text3)]">
        {t("privacyPrefs.loading")}
      </p>
    );
  }

  const visibilityHelp: Record<ProfileVisibility, string> = {
    public: t("privacyPrefs.visibilityHelpPublic"),
    followers: t("privacyPrefs.visibilityHelpFollowers"),
    private: t("privacyPrefs.visibilityHelpPrivate"),
  };
  const visibilityLabel: Record<ProfileVisibility, string> = {
    public: t("privacyPrefs.visibilityPublic"),
    followers: t("privacyPrefs.visibilityFollowers"),
    private: t("privacyPrefs.visibilityPrivate"),
  };

  return (
    <div>
      <h3 className="mb-1 text-[12px] font-bold text-[var(--color-text)]">
        {t("privacyPrefs.title")}
      </h3>
      <p className="mb-2 text-[11px] text-[var(--color-text3)]">
        {t("privacyPrefs.bodyPrefix")}
        <span className="font-mono">
          {t("privacyPrefs.bodyUsernamePlaceholder")}
        </span>
        {t("privacyPrefs.bodySuffix")}
      </p>

      {error && (
        <p
          role="alert"
          className="mb-2 text-[11px]"
          style={{ color: "var(--color-no)" }}
        >
          {error}
        </p>
      )}

      <div className="mb-3">
        <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
          {t("privacyPrefs.profileVisibility")}
        </label>
        <div
          role="radiogroup"
          aria-label={t("privacyPrefs.profileVisibilityAria")}
          className="flex flex-wrap gap-2"
        >
          {(["public", "followers", "private"] as const).map((v) => {
            const active = prefs.profileVisibility === v;
            const pending = pendingKey === `visibility:${v}`;
            return (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={pending || pendingKey !== null}
                onClick={() =>
                  applyPatch(
                    { profileVisibility: v },
                    `visibility:${v}`,
                  )
                }
                className="rounded-md border px-3 py-1.5 text-[11px] font-bold capitalize transition-colors disabled:opacity-50"
                style={{
                  borderColor: active
                    ? "var(--color-brand)"
                    : "var(--color-border)",
                  background: active
                    ? "var(--color-brand-tint)"
                    : "var(--color-bg)",
                  color: active
                    ? "var(--color-brand)"
                    : "var(--color-text2)",
                }}
              >
                {visibilityLabel[v]}
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-[10px] text-[var(--color-text3)]">
          {visibilityHelp[prefs.profileVisibility]}
        </p>
      </div>

      <ul className="space-y-1.5 text-[12px]">
        <PrivacyToggle
          label={t("privacyPrefs.showVolume")}
          checked={prefs.showVolume}
          pending={pendingKey === "showVolume"}
          onChange={() =>
            applyPatch({ showVolume: !prefs.showVolume }, "showVolume")
          }
        />
        <PrivacyToggle
          label={t("privacyPrefs.showWinRate")}
          checked={prefs.showWinRate}
          pending={pendingKey === "showWinRate"}
          onChange={() =>
            applyPatch(
              { showWinRate: !prefs.showWinRate },
              "showWinRate",
            )
          }
        />
        <PrivacyToggle
          label={t("privacyPrefs.showTradeHistory")}
          checked={prefs.showTradeHistory}
          pending={pendingKey === "showTradeHistory"}
          onChange={() =>
            applyPatch(
              { showTradeHistory: !prefs.showTradeHistory },
              "showTradeHistory",
            )
          }
        />
        {/* Slice 2A — verifiable position badge on comments. Default
            on (transparency norm for prediction markets). User can
            also override per-comment via the "Post anonymously"
            toggle on the composer; this flag is the global default. */}
        <PrivacyToggle
          label={t("privacyPrefs.showPositionOnComments")}
          checked={prefs.showPositionOnComments}
          pending={pendingKey === "showPositionOnComments"}
          onChange={() =>
            applyPatch(
              { showPositionOnComments: !prefs.showPositionOnComments },
              "showPositionOnComments",
            )
          }
        />
      </ul>
      <p className="mt-2 text-[10px] text-[var(--color-text3)]">
        {t("privacyPrefs.showPositionOnCommentsHelp")}
      </p>
    </div>
  );
}

function PrivacyToggle({
  label,
  checked,
  pending,
  onChange,
}: {
  label: string;
  checked: boolean;
  pending: boolean;
  onChange: () => void;
}) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-[var(--color-text2)]">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={pending}
        onClick={onChange}
        className="inline-flex h-5 w-9 items-center rounded-full border transition-colors disabled:opacity-50"
        style={{
          borderColor: checked
            ? "var(--color-brand)"
            : "var(--color-border)",
          background: checked ? "var(--color-brand)" : "var(--color-bg)",
        }}
      >
        <span
          className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform"
          style={{
            transform: checked ? "translateX(18px)" : "translateX(3px)",
            opacity: pending ? 0.5 : 1,
          }}
        />
      </button>
    </li>
  );
}

function SharePrefsSection() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState<SharePrefs | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .getSharePrefs()
      .then((p) => {
        if (!cancelled) setPrefs(p);
      })
      .catch(() => {
        // Silent — section just stays in loading state.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function toggle(key: keyof SharePrefs) {
    if (!prefs || pendingKey) return;
    const optimistic: SharePrefs = { ...prefs, [key]: !prefs[key] };
    setPrefs(optimistic);
    setPendingKey(key);
    setError(null);
    try {
      const updated = await api.updateSharePrefs({ [key]: optimistic[key] });
      setPrefs(updated);
    } catch (err) {
      setPrefs(prefs);
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("sharePrefs.saveFailed"),
      );
    } finally {
      setPendingKey(null);
    }
  }

  if (!prefs) {
    return (
      <p className="text-[11px] text-[var(--color-text3)]">
        {t("sharePrefs.loading")}
      </p>
    );
  }

  return (
    <div>
      <h3 className="mb-1 text-[12px] font-bold text-[var(--color-text)]">
        {t("sharePrefs.title")}
      </h3>
      <p className="mb-2 text-[11px] text-[var(--color-text3)]">
        {t("sharePrefs.body")}
      </p>

      {error && (
        <p
          role="alert"
          className="mb-2 text-[11px]"
          style={{ color: "var(--color-no)" }}
        >
          {error}
        </p>
      )}

      <ul className="space-y-1.5 text-[12px]">
        <ShareToggle
          label={t("sharePrefs.promptsEnabled")}
          checked={prefs.promptsEnabled}
          pending={pendingKey === "promptsEnabled"}
          onChange={() => toggle("promptsEnabled")}
        />
        <ShareToggle
          label={t("sharePrefs.showUsername")}
          checked={prefs.showUsername}
          pending={pendingKey === "showUsername"}
          onChange={() => toggle("showUsername")}
        />
        <ShareToggle
          label={t("sharePrefs.showAmount")}
          checked={prefs.showAmount}
          pending={pendingKey === "showAmount"}
          onChange={() => toggle("showAmount")}
        />
        <ShareToggle
          label={t("sharePrefs.showMarketTitle")}
          checked={prefs.showMarketTitle}
          pending={pendingKey === "showMarketTitle"}
          onChange={() => toggle("showMarketTitle")}
        />
      </ul>
    </div>
  );
}

function ShareToggle({
  label,
  checked,
  pending,
  onChange,
}: {
  label: string;
  checked: boolean;
  pending: boolean;
  onChange: () => void;
}) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-[var(--color-text2)]">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={pending}
        onClick={onChange}
        className="inline-flex h-5 w-9 items-center rounded-full border transition-colors disabled:opacity-50"
        style={{
          borderColor: checked
            ? "var(--color-brand)"
            : "var(--color-border)",
          background: checked ? "var(--color-brand)" : "var(--color-bg)",
        }}
      >
        <span
          className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform"
          style={{
            transform: checked ? "translateX(18px)" : "translateX(3px)",
            opacity: pending ? 0.5 : 1,
          }}
        />
      </button>
    </li>
  );
}

function Field({
  label,
  helpText,
  children,
}: {
  label: string;
  helpText: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
        {label}
      </label>
      {children}
      <p className="mt-1 text-[10px] text-[var(--color-text3)]">{helpText}</p>
    </div>
  );
}
